import { useState, useEffect } from "react";
import { AlertTriangle, Clock, Package, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { DriverApprovalDialog } from "./DriverApprovalDialog";
import { format } from "date-fns";

interface LatePackage {
  id: string;
  retailer: string;
  size: string;
  created_at: string;
  user_id: string;
  profiles?: {
    email: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  user_pickups?: {
    proposed_date: string;
    proposed_time_slot: string;
  } | null;
}

export const LateSubmissionsManager = () => {
  const [latePackages, setLatePackages] = useState<LatePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<LatePackage | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchLatePackages = async () => {
    try {
      const { data, error } = await supabase
        .from("packages")
        .select(`
          id,
          retailer,
          size,
          created_at,
          user_id,
          pickup_id,
          profiles!packages_user_id_fkey (
            email,
            first_name,
            last_name
          ),
          user_pickups (
            proposed_date,
            proposed_time_slot
          )
        `)
        .eq("requires_driver_approval", true)
        .is("driver_approved", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setLatePackages((data as any) || []);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching late packages:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatePackages();
  }, []);

  const handlePackageClick = (pkg: LatePackage) => {
    setSelectedPackage(pkg);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Late Submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (latePackages.length === 0) {
    return null; // Don't show the card if no late packages
  }

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-800">
        <CardHeader className="bg-amber-50 dark:bg-amber-950/30">
          <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-5 w-5" />
            Late Submissions Requiring Approval
            <Badge variant="secondary" className="ml-auto">
              {latePackages.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {latePackages.map((pkg) => {
              const profile = pkg.profiles;
              const pickup = pkg.user_pickups;
              const userName = profile
                ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.email
                : "Unknown";

              return (
                <div
                  key={pkg.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-full">
                      <Package className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium">{pkg.retailer}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-3 w-3" />
                        <span>{userName}</span>
                        <span>•</span>
                        <span>{pkg.size}</span>
                      </div>
                      {pickup && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          <span>
                            Pickup: {format(new Date(pickup.proposed_date), "MMM d")} at{" "}
                            {pickup.proposed_time_slot}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePackageClick(pkg)}
                  >
                    Review
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <DriverApprovalDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        packageId={selectedPackage?.id || null}
        packageInfo={
          selectedPackage
            ? {
                retailer: selectedPackage.retailer,
                size: selectedPackage.size,
                userName: selectedPackage.profiles
                  ? `${selectedPackage.profiles.first_name || ""} ${selectedPackage.profiles.last_name || ""}`.trim() ||
                    selectedPackage.profiles.email
                  : undefined,
              }
            : undefined
        }
        onSuccess={fetchLatePackages}
      />
    </>
  );
};
